import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

interface NavbarProps {
  children?: ReactNode;
}

interface NavbarSectionProps {
  children: ReactNode;
  className?: string;
}

interface NavbarItemProps {
  href: string;
  children: ReactNode;
  'aria-label'?: string;
  current?: boolean;
}

interface NavbarLabelProps {
  children: ReactNode;
}

/**
 * Main Navbar container with dark theme header
 */
export function Navbar({ children }: NavbarProps) {
  return (
    <nav className="sticky top-0 z-40 border-b border-zinc-950/10 bg-purple-900 dark:border-white/10 dark:bg-primary-950">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </nav>
  );
}

/**
 * Navbar section - groups navbar items
 */
export function NavbarSection({ children, className }: NavbarSectionProps) {
  return <div className={clsx('flex items-center gap-3', className)}>{children}</div>;
}

/**
 * Individual navbar item/link
 */
export function NavbarItem({ href, children, 'aria-label': ariaLabel, current }: NavbarItemProps) {
  const location = useLocation();
  const isActive = current !== undefined ? current : location.pathname === href;

  return (
    <Link
      to={href}
      aria-label={ariaLabel}
      className={clsx(
        'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary-800 text-white dark:bg-primary-800'
          : 'text-primary-300 hover:bg-primary-800 hover:text-white dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-white'
      )}
    >
      {children}
    </Link>
  );
}

/**
 * Navbar label for displaying text
 */
export function NavbarLabel({ children }: NavbarLabelProps) {
  return <span className="text-sm font-medium text-white">{children}</span>;
}

/**
 * Navbar spacer - pushes items to the right
 */
export function NavbarSpacer() {
  return <div className="flex-1" />;
}

/**
 * Navbar divider
 */
export function NavbarDivider({ className }: { className?: string }) {
  return <div className={clsx('h-6 w-px bg-primary-700 dark:bg-primary-800', className)} />;
}
